import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { Box } from "@mui/material";
import { useEffect, useRef } from "react";
import { PLAYGROUND_TRANSFORMERS } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/markdownTransformers";
import {
  flattenDataKeyReferencesToSnapshotValues,
  importMarkdownToLexical,
} from "src/components/DocViewer/components/markdownDocViewer/rawEditor/preprocessMarkdown";
import { editorConfig } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import { TableContext } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tablePlugin/TablePlugin";

interface MarkdownReaderProps {
  markdown: string;
  tableStyles?: Record<string, unknown>;
}

const MarkdownReaderInner = ({
  markdown,
  tableStyles,
}: MarkdownReaderProps) => {
  const [editor] = useLexicalComposerContext();
  const lastImportedRef = useRef<string | null>(null);

  useEffect(() => {
    editor.setEditable(false);
  }, [editor]);

  useEffect(() => {
    if (lastImportedRef.current === markdown) return;
    lastImportedRef.current = markdown;
    editor.update(() => {
      const flattened = flattenDataKeyReferencesToSnapshotValues(markdown);
      importMarkdownToLexical(flattened, PLAYGROUND_TRANSFORMERS);
    });
  }, [editor, markdown]);

  return (
    <TableContext>
      <Box
        className="editor-container"
        sx={{
          maxWidth: "100%",
          overflowX: "hidden",
          position: "relative",
          width: "auto",
          padding: "0",
          height: "100%",

          "& .tableScrollableWrapper": {
            marginBottom: "16px",
          },
          "& .tableScrollableWrapper:last-child": {
            marginBottom: "0",
          },
          "& .editor-input": {
            height: "100%",
            padding: "16px",
          },
          "& > .editor-inner": {
            height: "fit-content",
          },
          "& > .editor-inner > .editor-input": {
            height: "fit-content",
            whiteSpace: "normal",
          },
          "& > .editor-inner > .editor-input > .editor-paragraph:not(:last-child)":
            {
              marginBottom: "16px",
            },
          "& > .editor-inner > .editor-input > .editor-paragraph > span:last-child":
            {
              paddingBottom: "0",
            },
          "& .editor-inner .editor-input table": {
            tableLayout: "auto",
            ...(tableStyles || {}),
            width: tableStyles?.width || "100%",
            maxWidth: tableStyles?.maxWidth || "100%",
            minWidth: "0",
            display: "block",
            overflowX: "auto",
            boxSizing: "border-box",
            border: "none",
            "& tr > td > ul": {
              padding: "inherit",
            },
            "& tr > td > ol": {
              padding: "inherit",
            },
          },
        }}
      >
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="editor-input outline-none" />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ListPlugin />
          <HistoryPlugin />
          <TablePlugin hasHorizontalScroll={true} />
        </div>
      </Box>
    </TableContext>
  );
};

const MarkdownReader = (props: MarkdownReaderProps) => {
  return (
    <LexicalComposer
      initialConfig={{
        ...editorConfig,
        namespace: "markdown-reader",
        editable: false,
      }}
    >
      <MarkdownReaderInner {...props} />
    </LexicalComposer>
  );
};

export default MarkdownReader;
